import type { CustomerOrderChange } from '@/types/salesOrder'
import { fullName } from '@/components/UserAvatar'

/**
 * What each logged field is called in the product, rather than in the database. A field with no
 * entry here still reads: the key is turned into words, which is right often enough that listing
 * every one of them would be upkeep for no gain.
 */
const FIELD_LABELS: Record<string, string> = {
  'customer_order.customer': 'Customer',
  'customer_order_head.customer_order_number': 'Customer order number',
  'customer_order_head.customer_contact_person': 'Customer contact',
  'customer_order_head.supplier_contact_person': 'Our contact',
  'customer_order_head.goods_label': 'Goods label',
  'customer_order_head.additional_information': 'Additional information',
  'customer_order_head.payment_terms': 'Payment terms',
  'customer_order_head.delivery_terms': 'Delivery terms',
  'customer_order_head.delivery_method': 'Delivery method',
  'customer_order_head.requested_delivery_date': 'Requested delivery date',
  'customer_order_head.delivery_address_company_name': 'Delivery company',
  'customer_order_head.delivery_address_care_of': 'Delivery c/o',
  'customer_order_head.delivery_address_street': 'Delivery street',
  'customer_order_head.delivery_address_house_number': 'Delivery house number',
  'customer_order_head.delivery_address_postal_code': 'Delivery postal code',
  'customer_order_head.delivery_address_city': 'Delivery city',
  'customer_order_head.delivery_address_country': 'Delivery country',
  'customer_order_head.delivery_address_additional_information': 'Delivery address information',
  'customer_order_row.position': 'Position',
  'customer_order_row.matched_article': 'Article',
  'customer_order_row.quantity': 'Quantity',
  'customer_order_row.requested_delivery_date': 'Delivery date',
  'action:validated': 'Validated',
  'action:row_added': 'Added article',
  'action:row_removed': 'Removed article',
}

export function isActionEvent(change: CustomerOrderChange): boolean {
  return change.field_name.startsWith('action:')
}

export function fieldLabel(fieldName: string): string {
  const known = FIELD_LABELS[fieldName]
  if (known) return known

  const leaf = fieldName.split('.').pop() ?? fieldName
  const words = leaf.replace(/^action:/, '').split('_')
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

export function changedBy(change: CustomerOrderChange): string {
  if (!change.by) return 'Unknown'
  return fullName(change.by.first_name, change.by.last_name) || 'Unknown'
}

/**
 * One sentence saying what somebody did, for the hover on the overview.
 *
 * Written from what the entry actually holds: a field edit says which field, on which line, and
 * what it now reads; an emptied field says it was cleared rather than showing nothing after an
 * arrow. Anything the log cannot be read that plainly falls back to naming the person and the
 * time, which is the least the column has to answer for.
 */
export function describeChange(change: CustomerOrderChange): string {
  const who = changedBy(change)

  // An added row names the article it added and the line it went on, which is the whole of what
  // happened. The other actions are about the order as a whole and read that way.
  if (change.field_name === 'action:row_added') {
    const line = change.position ? ` as row ${change.position}` : ''
    return `${who} added article ${change.new_value}${line}`
  }

  // A removed row names what the order no longer carries, which is held in the old value since
  // there is no new one.
  if (change.field_name === 'action:row_removed') {
    const line = change.position ? ` from row ${change.position}` : ''
    return `${who} removed article ${change.old_value}${line}`
  }

  if (isActionEvent(change)) {
    return `${who} ${fieldLabel(change.field_name).toLowerCase()} the order`
  }

  const label = fieldLabel(change.field_name)
  const line = change.position && change.position !== 'HEAD' ? ` on row ${change.position}` : ''

  if (!change.new_value) {
    return `${who} cleared ${label.toLowerCase()}${line}`
  }

  return `${who} set ${label.toLowerCase()}${line} to ${change.new_value}`
}

/** The moment, in the reader's own timezone and locale. */
export function formatChangeTime(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed)
}

/** The fallback the hover uses when a change cannot be described any better than by who and when. */
export function lastEditedBy(change: CustomerOrderChange): string {
  return `Last edited by ${changedBy(change)} at ${formatChangeTime(change.timestamp)}`
}
